import { useEffect, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { MethodPicker } from "../../../../utils/form";
import _, { castArray, isEmpty } from "lodash";
import NavigationBar from "../../../../utils/wizard/navigation";
import { useForm, FormProvider } from "react-hook-form";
import { defaultOrderTypesConfig, practicesOrderTypesConfig } from "./defaultOrderTypesConfig";
import HomologarOrders from "../components/Orders";
import { useApi, useHealthCenterRules } from "../../../../utils/hooks";
import { getTemplateTypes, procedureTypeFromTemplateType } from "../../../utils/homologar/templates/utils";
import HomologarControls from "../components/Controls";
import LaborCertificateChip from "../../../orders/components/LaborCertificateChip";
import { LABOR_CERTIFICATE, captureLaborCertificateClick } from "../../../orders/laborCertificate";
import {
  defaultMedicine,
  getDefaultOrders,
  MAX_ORDERS,
} from "../utils/constants";
import {
  manageSetNewItem as _manageSetNewItem,
  removeEmptyString,
  isThereAtLeastOneFilledFormOrder,
  mapOrderToForm,
  isEmptyOrder,
  itemKey,
  getCardMaxItems,
  groupItemsByProcedureType,
  planCardChunks,
  planCardsPlacement,
} from "../utils/orders";
import { OrderType, ItemType } from "../../../utils/homologar/search/utils/constants";
import { ErrorAlert, WarningAlert } from "../../../../utils";
import { getEventNomenclatureEventHandler } from "../../../utils/homologar/search/utils/events";

const OrdersForm = ({ onFinish, ...props }) => {
  const recetarioApi = useApi();
  const healthCenterOrdersRules = useHealthCenterRules("orders.homologar");
  // Una sola noción de `version` maneja UI y motor de búsqueda, todo junto:
  //   v1 -> UI tabbed + motor viejo (JSON/Fuse)
  //   v2 -> UI unificada ("Prácticas") + motor viejo
  //   v3 -> UI unificada + motor nuevo (ProcedureMatcher desde la DB)
  const version = healthCenterOrdersRules?.version;
  // v2 y v3 comparten la misma UI unificada (isV2 = "usa la UI de Prácticas").
  const isV2 = version === "v2" || version === "v3";
  const appVersion = isV2 ? version : "v1";
  // Motor nuevo (ProcedureMatcher desde la DB) en v3.
  const isNomenclatureV3 = version === "v3";
  // Cuántas prácticas de imagen entran por card/orden. Se lee de la MISMA config que
  // v1: `orders.homologar.typesConfig[value:"imaging"].maxItemsPerOrder`. 1 (default) =
  // cada imagen en su propia card/pdf; `null` = sin límite (unificadas, ej. Tandil).
  // Así un HC configurado para v1 unifica también en v3 sin duplicar config. `null`
  // se respeta (no se pisa con el default: se distingue "no configurado" de "sin límite").
  const imagingTypeConfig = _.find(healthCenterOrdersRules?.typesConfig, {
    value: OrderType.IMAGING,
  });
  const imagingMaxItemsPerCard =
    imagingTypeConfig && "maxItemsPerOrder" in imagingTypeConfig
      ? imagingTypeConfig.maxItemsPerOrder
      : 1;

  const [orderTypes, setOrderTypes] = useState(defaultOrderTypesConfig);
  /**
   * OrderType {
   *  label: string,
   *  value: OrdersType,
   *  order: number,
   *  disabled?: boolean,
   *  maxItemsPerOrder: number | null,
   * }
   */
  // Arranca ya con el orderType correcto según la UI (v2 = "Prácticas"), así el fetch
  // de plantillas no dispara dos veces (default -> configurado) al montar.
  const [selectedOrderType, setSelectedOrderType] = useState(
    (isV2 ? practicesOrderTypesConfig : defaultOrderTypesConfig)[0]
  );

  /**
   * number or null (if null, no limit)
   */
  const [maxItemsPerOrder, setMaxItemsPerOrder] = useState(null);
  /**
   * Each order must be
   * {
   *   items: { name: string }[],
   *   clarifications: string,
   *   template?: Template,
   *   procedureType?: string | null,
   * }
   */
  const [orders, setOrders] = useState(getDefaultOrders(isV2, selectedOrderType.value));
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [error, setError] = useState();
  const [warning, setWarning] = useState();

  const form = useForm({
    defaultValues: {
      medicines: [defaultMedicine],
      orderType: selectedOrderType.value,
    },
  });
  const { handleSubmit, setValue, watch } = form;
  const currentForm = watch();
  const manageSetNewItem = (order) =>
    _manageSetNewItem(order, setOrders, maxItemsPerOrder, isV2, imagingMaxItemsPerCard);

  useEffect(() => {
    setMaxItemsPerOrder(selectedOrderType.maxItemsPerOrder);
  }, [selectedOrderType]);

  useEffect(() => {
    setValue("orderType", selectedOrderType.value);
    const templateTypes = getTemplateTypes(selectedOrderType.value, isNomenclatureV3);
    // Al cambiar de tipo no puede quedar nada del anterior: ni las plantillas ni la
    // seleccionada, ni la respuesta tardía de un fetch viejo pisando a la nueva
    // (una plantilla homologar bajo el orderType manual rompe extractItems).
    let stale = false;
    setTemplates([]);
    setSelectedTemplate(null);
    recetarioApi
      .templates(templateTypes)
      .catch(() => [])
      .tap((fetched) => {
        if (!stale) setTemplates(fetched);
      });
    return () => {
      stale = true;
    };
    // Se refetchea solo cuando cambia el TIPO (value) o el motor, no cada vez que
    // selectedOrderType cambia de referencia de objeto (evita un GET de más).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderType.value, isNomenclatureV3]);

  useEffect(() => {
    const needsDefault = !orders || orders.length === 0;
    if (needsDefault && getDefaultOrders(isV2, selectedOrderType.value).length > 0) {
      setOrders(getDefaultOrders(isV2, selectedOrderType.value));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders]);

  useEffect(() => {
    const baseConfig = isV2 ? practicesOrderTypesConfig : defaultOrderTypesConfig;
    const healthCenterOrderTypesConfig = healthCenterOrdersRules?.typesConfig;
    const orderTypesConfig = _.map(baseConfig, (type) =>
      _.merge({}, type, _.find(healthCenterOrderTypesConfig, { value: type.value }))
    );
    const types = orderTypesConfig.sort((a, b) => a.order - b.order);
    const selectedType =
      orderTypesConfig.filter((t) => !t.disabled)[0] ?? baseConfig[0];
    setOrderTypes(types);
    setSelectedOrderType(selectedType);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthCenterOrdersRules]);

  useEffect(() => {
    setValue("medicines", [defaultMedicine]);
    const fallbackOrderType =
      isV2 && selectedOrderType.value === OrderType.PRACTICES
        ? OrderType.MANUAL
        : selectedOrderType.value;
    orders.forEach((order, index) => {
      setValue(`medicines[${index}]`, mapOrderToForm(order, fallbackOrderType));
    });
  }, [orders, selectedOrderType, setValue, isV2]);

  const addEmptyOrder = () => {
    setOrders((prev) => [
      ...prev,
      { items: [], clarifications: "", template: null, procedureType: null },
    ]);
  };

  const setSelectedOrderTypeByValue = (value) => {
    const orderType = orderTypes.find((t) => t.value === value);
    if (!orderType) return;
    setSelectedOrderType(orderType);
  };

  const addManualOrderItems = (items, template) => {
    const itemsArray = castArray(items)
      .map((i) => removeEmptyString(i.name))
      .filter((name) => !isEmpty(name));

    if (itemsArray.length === 0) return;

    const clarifications = itemsArray.map((item) => `- ${item}`).join("\n");

    manageSetNewItem({ items: [], clarifications, template });
  };

  // `reservedCards` cuenta cards que ya "gastaron" llamadas previas dentro del
  // MISMO addOrderItemsByTemplate (ver ahí abajo) — DEV-1687: al agregar una
  // plantilla mixta se llama una vez por procedureType, y `orders` (closure)
  // sigue reflejando el estado de ANTES de aplicar la plantilla en todas esas
  // llamadas (React no lo actualiza en el medio). Sin este acumulador, el chequeo
  // de MAX_ORDERS de un grupo no ve las cards que un grupo hermano ya reservó en
  // este mismo tick y puede creer que hay lugar cuando no lo hay — el ítem se
  // pierde en silencio (el guard real, en manageSetNewItemV2, no avisa, sólo no
  // agrega). Devuelve cuántas cards nuevas reservó, para que el llamador acumule.
  const addNomenclatureOrderItems = (items, template = null, procedureType = null, reservedCards = 0) => {
    const requestedItems = castArray(items);
    if (requestedItems.length === 0) return 0;

    // Dedupe contra TODAS las cards existentes, no solo la que recibe el ítem:
    // a diferencia de `dedupeItems` (por card), esto evita duplicados cuando el
    // mismo procedureId ya está en otra card — ej. agregado a mano y luego
    // aplicado de nuevo vía plantilla (o viceversa). Las imágenes en particular
    // nunca comparten card (1 ítem = 1 card), así que ahí el dedupe por card
    // nunca alcanza a detectar el repetido.
    const existingKeys = new Set(orders.flatMap((o) => o.items.map(itemKey)));
    const itemsArray = requestedItems.filter((item) => !existingKeys.has(itemKey(item)));

    if (itemsArray.length === 0) {
      setWarning("Esta práctica ya fue agregada a la solicitud.");
      return 0;
    }

    // v1: límite por orderType (`typesConfig[].maxItemsPerOrder`). v2: límite por
    // card según procedureType (imaging usa `imagingMaxItemsPerCard`, laboratorio
    // no tiene límite) — necesario para no exceder el tamaño de card configurado
    // cuando llega un lote grande de una sola vez (ej. plantilla SET con varios
    // ítems de imagen seleccionados).
    const effectiveMaxPerCard = isV2
      ? getCardMaxItems(procedureType, imagingMaxItemsPerCard)
      : maxItemsPerOrder;

    const nonEmptyOrdersCount = orders.filter((o) => !isEmptyOrder(o)).length;
    const { chunks } = planCardChunks(itemsArray, effectiveMaxPerCard, nonEmptyOrdersCount, reservedCards);

    if (!chunks) {
      setError(`El máximo de ordenes permitidas es de ${MAX_ORDERS}`);
      return 0;
    }

    chunks.forEach((chunk) => {
      manageSetNewItem({ items: chunk, clarifications: "", template, procedureType });
    });
    // Con `template` seteado (siempre el caso acá), la única forma de mergear en
    // vez de abrir card nueva es la última card vacía (`lastIsEmpty` en
    // manageSetNewItemV2) — y eso "consume" esa misma card igual (pasa de vacía a
    // ocupada). Por eso cada chunk cuenta como 1 card reservada sin importar si
    // mergeó o abrió una nueva.
    return chunks.length;
  };

  const templateFallbackProcedureType = (template) =>
    template?.procedureType ?? procedureTypeFromTemplateType(template?.type) ?? null;

  const planItemsInOrder = (items, template = null, fallbackProcedureType = null) => {
    const existingKeys = new Set(orders.flatMap((o) => o.items.map(itemKey)));
    const newItems = castArray(items).filter((item) => !existingKeys.has(itemKey(item)));
    const groups = groupItemsByProcedureType(newItems, fallbackProcedureType).map((group) => ({
      ...group,
      template,
    }));
    return { newItems, ...planCardsPlacement(orders, groups, imagingMaxItemsPerCard) };
  };

  const addItemsToCards = (items, template = null, fallbackProcedureType = null) => {
    const { newItems, orders: plannedOrders, fits } = planItemsInOrder(items, template, fallbackProcedureType);
    if (newItems.length === 0) {
      setWarning("Esta práctica ya fue agregada a la solicitud.");
      return;
    }
    if (!fits) {
      setError(`El máximo de ordenes permitidas es de ${MAX_ORDERS}`);
      return;
    }
    setOrders(plannedOrders);
  };

  const templateItemsFitInOrder = (items, template) =>
    planItemsInOrder(items, template, templateFallbackProcedureType(template)).fits;

  const onSelectedItem = (item) => {
    if (!item || !item.itemType) return;

    if (item.itemType === ItemType.NOMENCLATURE) {
      if (isV2) {
        addItemsToCards([item], null, item.procedureType ?? null);
        return;
      }
      addNomenclatureOrderItems([item], null, item.procedureType ?? null);
    } else {
      const template = templates.find((t) => t.name === item.name);
      if (!template || (!template.items && !template.content)) return;
      setSelectedTemplate(template);
    }
  };

  const handleChangeOrderType = (event, newValue) => {
    setSelectedOrderTypeByValue(newValue);
    setOrders(getDefaultOrders(isV2, newValue));
  };

  const onSubmit = (medicines) => {
    getEventNomenclatureEventHandler(
      selectedOrderType.value,
      appVersion
    ).handleNoSelectedOptionOrders(orders);
    onFinish(medicines);
    props.nextStep();
  };

  const addOrderItemsByTemplate = (items, template) => {
    if (selectedOrderType.value === OrderType.MANUAL) {
      addManualOrderItems(items, template);
      return;
    }

    // DEV-1687: en v3 una plantilla ya puede mezclar imaging y laboratory —
    // cada ítem sabe su propio tipo (hidratado desde la API), así que se
    // agrupan por tipo antes de agregarlos, para que terminen en cards
    // separadas con el mismo agrupamiento que el flujo manual (laboratorio
    // todas juntas, imaging según imagingMaxItemsPerCard).
    const fallbackProcedureType = templateFallbackProcedureType(template);
    if (isV2) {
      addItemsToCards(items, template, fallbackProcedureType);
      return;
    }
    let reservedCards = 0;
    groupItemsByProcedureType(items, fallbackProcedureType).forEach(
      ({ procedureType, items: groupItems }) => {
        reservedCards += addNomenclatureOrderItems(groupItems, template, procedureType, reservedCards);
      }
    );
  };

  const handleLaborCertificateClick = () => {
    captureLaborCertificateClick(selectedOrderType.value);
    if (selectedOrderType.value !== OrderType.MANUAL) {
      setSelectedOrderTypeByValue(OrderType.MANUAL);
      setOrders([{ items: [], clarifications: LABOR_CERTIFICATE.content, template: null, procedureType: null }]);
    } else {
      manageSetNewItem({ items: [], clarifications: LABOR_CERTIFICATE.content, template: null });
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Stack spacing="1rem">
          <Typography
            variant="title"
            sx={{
              display: "flex",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            Solicitud
          </Typography>
          <MethodPicker
            method={selectedOrderType.value}
            setMethod={setSelectedOrderTypeByValue}
            handleChangeTabs={handleChangeOrderType}
            options={orderTypes}
          />
          <Stack spacing={2}>
            <HomologarControls
              orderType={selectedOrderType.value}
              onSelectedItem={onSelectedItem}
              templates={templates}
              orders={orders}
              addEmptyOrder={addEmptyOrder}
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              setTemplates={setTemplates}
              addOrderItems={addOrderItemsByTemplate}
              maxItemsPerOrder={maxItemsPerOrder}
              imagingMaxItemsPerCard={isV2 ? imagingMaxItemsPerCard : undefined}
              templateItemsFitInOrder={isV2 ? templateItemsFitInOrder : undefined}
              isV2={isV2}
              appVersion={appVersion}
              isNomenclatureV3={isNomenclatureV3}
              belowSearch={<LaborCertificateChip onClick={handleLaborCertificateClick} />}
            />
            <HomologarOrders
              orders={orders}
              setOrders={setOrders}
              form={form}
              fieldRequired={!isThereAtLeastOneFilledFormOrder(currentForm)}
              isV2={isV2}
              orderType={selectedOrderType.value}
              {...props}
            />
          </Stack>
        </Stack>

        <NavigationBar
          nextDisabled={!isThereAtLeastOneFilledFormOrder(currentForm)}
          {...props}
        />
        <ErrorAlert
          show={!_.isEmpty(error)}
          onClose={() => setError(null)}
          message={error}
        />
        <WarningAlert
          show={!_.isEmpty(warning)}
          onClose={() => setWarning(null)}
          message={warning}
        />
      </form>
    </FormProvider>
  );
};

export default OrdersForm;
